import {
  onMounted, computed, nextTick, ref,
} from 'vue';
import { useStore } from 'vuex';
import { fireTag } from '@common-vue/util/src/modules/tagManager';
import useThumbnailEligibility from '@modules/featureControl/composables/useThumbnailEligibility';
import useFeatureEligibility from '@modules/featureControl/composables/useFeatureEligibility';
import useEnvParams from '@composables/useEnvParams';
import useRouting from '@modules/router/composables/useRouting';

export default function useProductQuickviewSlideout() {
  // declarations
  const { isMcom, isBcom } = useEnvParams();
  const { isSimplificationOn, simplificationWithV1 } = useThumbnailEligibility();
  const { isBuyNowCtaEnabled, isPricingSpecialsBadgeEnabled } = useFeatureEligibility();
  const { onRouteChange } = useRouting();

  // declarations
  const store = useStore();
  const initialPageUtag = ref(null);

  // computations
  const imageHoveringData = computed(() => store.state.sortableGrid.imageHoveringData);
  const selectedColorData = computed(() => store.state.sortableGrid.selectedColor);
  const productQuickviewProps = computed(() => ({
    pageType: 'plp search',
    isBuyNowCtaEnabled: isBuyNowCtaEnabled.value,
    isPricingSpecialsBadgeEnabled: isPricingSpecialsBadgeEnabled.value,
    isPricingSimplification: isSimplificationOn.value,
    isPercentageStyleUpdate: isSimplificationOn.value,

    ...(isMcom && {
      isMiddlePriceRemoved: false,
      isWordCodeEnabled: true,
    }),
    ...(isBcom && {
      isPriceSimplificationTreatment: isSimplificationOn.value,
      isPriceSimplificationRedesignTreatment: simplificationWithV1.value,
    }),

    // tagging
    tagging: {
      onDrawerLoad: {
        quick_view_type: 'plp quick shop drawer',
      },
      onProductDetailClick: {
        event_name: 'plp quick shop:pdp see full details',
        link_name: 'plp quick shop:pdp see full details',
      },
      onAddToBagRequest: {
        bag_add_location: 'plp quick shop',
      },
    },
  }));

  // actions
  const _captureInitialPageUtag = () => {
    // Distributing data instead of keeping references that would change it from initial state
    initialPageUtag.value = { ...(window?.utag_data || {}) };
  };

  const isProductNotExcluded = ({ detail = {} }) => {
    const isMasterProduct = detail?.flags?.masterProduct || false;
    const isGWP = (detail?.typeName === 'GWP');
    const isGiftCard = ['VIRTUAL_GIFT_CARD', 'ELECTRONIC_GIFT_CARD'].indexOf(detail?.typeName) > -1;
    return !isMasterProduct && !isGWP && !isGiftCard;
  };

  const setSelectedColorSwatchId = (productId, colorId) => {
    store.commit('SET_SELECTED_COLOR', { productId, colorId });
  };

  const setImageHover = (data) => {
    store.commit('SET_IMAGE_HOVERING_DATA', data);
  };

  const getColorId = (product) => ((selectedColorData.value?.productId === product.id)
    ? selectedColorData.value.colorId
    : product?.traits?.colors?.selectedColor?.id || null);

  const openSlideoutPQV = (product) => {
    if (store.state?.productQuickviewStore && store.state?.slideoutStore) {
      store.dispatch('productQuickviewStore/toggle', {
        productId: product?.id,
        slideoutTitle: 'Quick shop',
        swatchColor: getColorId(product),
      });
    } else {
      console.warn('info', `ProductQuickview on PLP Search Grid is unable to open: product_id: ${product.id}`); // eslint-disable-line no-console
    }
  };

  /**
   * Refiring pageView tag in order to reset to original
   */
  const closeSlideoutPQV = () => {
    /* istanbul ignore else */
    if (initialPageUtag.value?.page_type && initialPageUtag.value?.page_type !== 'product quickview') {
      fireTag('view', initialPageUtag.value);
    }
  };

  // listeners
  onRouteChange(() => {
    nextTick(() => {
      initialPageUtag.value = { ...(window?.utag_data || {}) };
    });
  });

  onMounted(() => {
    _captureInitialPageUtag();
  });

  return {
    isProductNotExcluded,
    imageHoveringData,
    setImageHover,
    setSelectedColorSwatchId,
    productQuickviewProps,
    openSlideoutPQV,
    closeSlideoutPQV,
  };
}
